/**
 * 사용자 관리 화면의 커스텀 셀렉트 — 관리 콘솔 `sections/MenuControls.tsx` 의 `StyledSelect` 를
 * 그대로 옮겨 온 것이다 (근태 › 사용자 관리, 2026-08-25).
 *
 * 콘솔 원본에서 가져온 이유: 이 화면은 콘솔 화면의 **복사본**이라 셀렉트 모양까지 같아야 한다.
 * 원본은 아이콘 피커·아이콘 렌더러와 한 파일에 있는데 그 둘은 이 화면이 쓰지 않으므로 뺐다.
 * 스타일(`svc-select*`)은 `UserManage.css` 에 함께 옮겨 뒀다(8443 에는 같은 이름의 규칙이 없다).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from '../../icons';

export interface SelectOption { value: string; label: string }

/**
 * 커스텀 스타일 셀렉트(네이티브 select 미사용).
 *
 * `portal` 활성 시 드롭다운 메뉴를 document.body 로 createPortal + position:fixed 로 렌더한다.
 * → 부모의 overflow:hidden / 스크롤 컨테이너 / stacking context 에 잘리지 않고 **항상 최상단**에
 *   완전히 보인다. 아래 공간이 부족하면 위로 flip 하고, maxHeight 를 뷰포트에 맞춰 콘텐츠가
 *   항상 보이도록 한다. portal 미지정 시 기존 absolute 동작 그대로(회귀 0).
 */
export function StyledSelect({ value, options, onChange, disabled, placeholder, portal }: {
  value: string; options: SelectOption[]; onChange: (v: string) => void; disabled?: boolean; placeholder?: string; portal?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const cur = options.find((o) => o.value === value);

  // 버튼 위치 기준 fixed 좌표 계산 + 공간 부족 시 위로 flip.
  const place = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
    const maxH = Math.max(120, Math.min(320, (openUp ? spaceAbove : spaceBelow) - gap - 8));
    setMenuStyle({
      position: 'fixed',
      left: Math.round(r.left),
      width: Math.round(r.width),
      maxHeight: maxH,
      zIndex: 2000,
      ...(openUp
        ? { bottom: Math.round(window.innerHeight - r.top + gap) }
        : { top: Math.round(r.bottom + gap) }),
    });
  }, []);

  useLayoutEffect(() => {
    if (open && portal) place();
  }, [open, portal, place]);

  // 열려 있는 동안 스크롤/리사이즈에 위치 추종 + 외부 클릭/ESC 로 닫기(메뉴·버튼 클릭은 제외).
  useEffect(() => {
    if (!open) return;
    const reposition = () => { if (portal) place(); };
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    if (portal) {
      window.addEventListener('scroll', reposition, true);
      window.addEventListener('resize', reposition);
    }
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, portal, place]);

  const menu = (
    <ul
      ref={menuRef}
      className={`svc-select__menu${portal ? ' svc-select__menu--portal' : ''}`}
      role="listbox"
      style={portal ? menuStyle : undefined}
    >
      {options.map((o) => (
        <li key={o.value} role="option" aria-selected={o.value === value}
          className={`svc-select__opt${o.value === value ? ' is-active' : ''}`}
          onClick={() => { onChange(o.value); setOpen(false); }}>
          <span>{o.label}</span>
          {o.value === value && <Check size={14} />}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="svc-select" ref={wrapRef}>
      <button ref={btnRef} type="button" className="svc-select__btn" disabled={disabled} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}>
        <span className={`svc-select__val${cur ? '' : ' is-placeholder'}`}>{cur?.label ?? placeholder ?? '선택'}</span>
        <ChevronDown size={16} className={`svc-select__chev${open ? ' is-open' : ''}`} />
      </button>
      {open && (portal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}

